import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@telegram-apps/telegram-ui';
import { getPage } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import styles from './RecycleRecordModal.module.css';

type RecycleRecord = {
  id: number;
  batchNo: string;
  gameName: string;
  gameId: number;
  cdk: string;
  steamId: string;
  price: number;
  proxyName: string;
  createdAt: string;
};

const PERIOD_LABEL: Record<number, string> = {
  1: '今日',
  2: '昨日',
  3: '近七天',
  4: '本月',
  5: '上月',
};

const AVATAR_COLORS = ['#1677ff', '#7257e8', '#ff9d27', '#e5484d', '#20a9b5'];

const colorOfName = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = hash * 31 + name.charCodeAt(i);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  period: number;
  nested?: boolean;
};

export const RecycleRecordModal = ({ open, onOpenChange, period, nested }: Props) => {
  const [records, setRecords] = useState<RecycleRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPage<RecycleRecord>(
        `/admin/tel/recycle/records?period=${period}&limit=100`
      );
      setRecords(result.records);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (open) {
      fetchRecords();
    }
  }, [open, fetchRecords]);

  const label = PERIOD_LABEL[period] ?? '';

  return (
    <Modal
      nested={nested}
      open={open}
      onOpenChange={onOpenChange}
      className="app-modal"
      header={
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>{label}回收记录</div>
          <div className={styles.bannerSub}>共 {total} 条回收记录</div>
          <div className={styles.bannerBars} aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <span className="spinner" />
          </div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : records.length > 0 ? (
          <div className={styles.list}>
            {records.map((record) => {
              const name = record.gameName || `游戏 #${record.gameId}`;
              return (
                <div key={record.id} className={styles.item}>
                  <span
                    className={styles.avatar}
                    style={{ background: colorOfName(name) }}
                  >
                    {firstChineseChar(name)}
                  </span>
                  <div className={styles.itemMain}>
                    <div className={styles.itemTop}>
                      <span className={styles.name}>{name}</span>
                      <span className={styles.time}>{record.createdAt}</span>
                    </div>
                    <div className={styles.itemBottom}>
                      <span className={styles.proxy}>{record.proxyName || '—'}</span>
                      <span className={styles.cdk}>{record.cdk}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.empty}>暂无回收记录</div>
        )}
      </div>
    </Modal>
  );
};
